import { Fragment, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Copy, Plus, Trash2 } from 'lucide-react';
import {
  applyOpeningVariant,
  MERGE_VARIABLE_LABELS,
  MERGE_VARIABLES,
  plainTextToHtml,
  renderTemplate,
  type BodyFormat,
  type MergeContext,
} from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VariableChip, VariableInput } from '@/components/VariableField';
import RichBodyEditor from '@/components/RichBodyEditor';
import { MailFrame } from '@/components/MailFrame';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TimelineTemplateCard from '@/components/TimelineTemplateCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * Template management (PRD §F3).
 *
 * The composer is the product's voice, and until now a planner was stuck with
 * the five starter templates — a screen that reads "written by software" is
 * exactly what this product is supposed to prevent. Templates are per-account
 * and editable; this is where that happens.
 *
 * Three things the editor does that a plain form would not:
 *
 *  - **Variables are chips, not syntax.** An unknown variable is rejected at
 *    the API, so `{{vendorname}}` is a send-time failure. The palette drops a
 *    real one in at the caret and the field draws it as a chip, which is both
 *    cheaper than explaining the error and less alarming than a screen of
 *    double braces in a field that is supposed to hold a friendly email.
 *  - **The preview is the whole email, not the template.** It runs the same
 *    `renderTemplate` the composer runs, appends the signature the server
 *    appends, and renders the result into an un-Tailwinded frame — body then
 *    signature, one document, the way it lands in an inbox. Half an email in
 *    app styling is a preview a planner cannot trust, and a separate box
 *    captioned "your signature will be added" is not a preview of anything. A
 *    token the API would reject stays visible as the raw `{{...}}` it is, which
 *    is the whole signal, and a banner restating it was noise.
 *  - **Opening variants are first-class.** Ten byte-identical bodies landing at
 *    ten vendors in one afternoon is a deliverability problem, and the editor
 *    says so rather than leaving the field unexplained.
 *
 * Two kinds of template live here, under a tab each. The inquiry templates
 * above are the page's reason to exist; the day-of ladder every new wedding is
 * drafted from is the other thing a planner means by "my template", and it sat
 * on Settings until it was moved here. They share nothing technically — one is
 * email, one is a list of offsets — but a planner looking for either looks in
 * the same place, which is the only argument that matters.
 */

type Template = {
  id: string;
  name: string;
  category: string | null;
  subject: string;
  body: string;
  bodyFormat: BodyFormat;
  openingVariants: string[];
  isDefault: boolean;
};

const ALL_CATEGORIES = '__all__';

/** A complete, plausible wedding, so the preview reads like real mail and any
 *  issue it surfaces is a genuine problem with the template rather than a gap
 *  in the sample. */
const SAMPLE: MergeContext = {
  vendor_name: 'Amy',
  vendor_business: 'Bloom & Bower Floral',
  // The full names, matching what the composer actually sends — `{{couple_names}}`
  // resolves to `coupleFullName`, not the short name the UI shows elsewhere.
  couple_names: 'Emma Smith & Liam Nguyễn',
  wedding_date: 'June 12, 2027',
  wedding_hours: '4:00 PM – 11:00 PM',
  // The sample florist's own window, not the couple's hours above — a vendor
  // is booked for part of the day, not all of it. Present so the editor's
  // preview renders the variable rather than chipping it as still blank: the
  // real composer resolves it per recipient from the wedding's coverage
  // windows, and there is no recipient on this page to resolve it from.
  coverage_hours: '11:00 AM – 8:00 PM',
  venue: 'Hollow Creek Barn',
  guest_count: '120',
  planner_name: 'Dana Whitfield',
  planner_business: 'Wildwood Events',
};

/** The vendor the sample is addressed to, for the header the preview draws. No
 *  address: `SAMPLE` invents a florist, and inventing an inbox for her would be
 *  the one line of the header a planner might mistake for real data. */
const SAMPLE_VENDOR = `${SAMPLE.vendor_name} · ${SAMPLE.vendor_business}`;

/** Matching the wedding hub's tabs: the only other tabbed page in the app. */
const TAB_CLASS =
  'label-caps flex-none px-0 text-xs text-foreground/70 data-[state=active]:after:bg-primary';

/**
 * Which of the two templates the URL is asking for.
 *
 * Only the timeline is spelled out. Inquiry templates are what this page is,
 * so an absent param, a stale `/templates` link and a typo all land there
 * rather than on the ladder; `?view=inquiries` is never written.
 */
const VIEW_PARAM = 'view';
const TIMELINE_VIEW = 'timeline';
const INQUIRIES_VIEW = 'inquiries';
/** Which inquiry template the editor is showing. See `selectTemplate`. */
const TEMPLATE_PARAM = 'template';

const BLANK: Omit<Template, 'id'> = {
  name: '',
  category: null,
  subject: '',
  body: '<p>Hi {{vendor_name}},</p><p>{{opening}}</p><p></p>',
  bodyFormat: 'html',
  openingVariants: [],
  isDefault: false,
};

export default function TemplatesPage() {
  const queryClient = useQueryClient();

  // In the URL rather than in state: a planner editing their day-of ladder
  // reloads, bookmarks and pastes that screen like any other. Replace, not
  // push — flicking between two tabs of one page is not history.
  const [params, setParams] = useSearchParams();
  const view = params.get(VIEW_PARAM) === TIMELINE_VIEW ? TIMELINE_VIEW : INQUIRIES_VIEW;
  const openView = (next: string) => {
    const nextParams = new URLSearchParams(params);
    if (next === TIMELINE_VIEW) nextParams.set(VIEW_PARAM, TIMELINE_VIEW);
    else nextParams.delete(VIEW_PARAM);
    setParams(nextParams, { replace: true });
  };

  /**
   * The open template lives in the URL rather than beside it, because the rows
   * are links and a link needs somewhere to point. Holding it in state too
   * would be two copies free to disagree — the "the link opened the right
   * template but the editor is showing another one" bug.
   *
   * Replace, not push, for the same reason as the tab above: moving down a list
   * of five templates is not five pages of history. It also means Back never
   * walks between templates, so Back cannot discard a draft the dialog below
   * exists to protect.
   */
  const selectedId = params.get(TEMPLATE_PARAM);
  const selectTemplate = (id: string | null) => {
    const nextParams = new URLSearchParams(params);
    if (id) nextParams.set(TEMPLATE_PARAM, id);
    else nextParams.delete(TEMPLATE_PARAM);
    setParams(nextParams, { replace: true });
  };
  /** What the row's `href` is, and what a planner copies out of the address bar. */
  const templateHref = (id: string) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set(TEMPLATE_PARAM, id);
    return `/templates?${nextParams.toString()}`;
  };

  const [draft, setDraft] = useState<Omit<Template, 'id'>>(BLANK);
  const [dirty, setDirty] = useState(false);
  /** The row clicked while the draft still had unsaved edits in it, parked
   *  until the planner says what should happen to them. Non-null opens the
   *  dialog at the bottom of the editor. */
  const [pendingId, setPendingId] = useState<string | null>(null);

  const { data: templates, isLoading } = useQuery<Template[]>({
    queryKey: ['templates', ''],
    queryFn: async () => {
      const res = await client.api.templates.$get({ query: { category: '' } });
      if (!res.ok) throw new Error('Failed to load templates');
      return res.json();
    },
  });

  // Only for the preview: the signature is the server's to append, but a
  // preview that stops at the last typed line is a preview of most of the
  // email. Templates are edited against what a vendor receives, and what a
  // vendor receives ends with this block.
  const { data: settings } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const res = await client.api['user-settings'].me.$get();
      if (!res.ok) throw new Error('Failed to load settings');
      return res.json();
    },
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return res.json();
    },
  });

  // Land on something rather than an empty right-hand pane — including when the
  // URL names a template that is gone, which is what a link shared last week
  // looks like. An empty editor would read as a broken page rather than as a
  // stale link, so the page opens on the first row and corrects the URL to it.
  useEffect(() => {
    if (!templates || templates.length === 0) return;
    if (selectedId && templates.some((t) => t.id === selectedId)) return;
    selectTemplate(templates[0]!.id);
  }, [templates, selectedId]);

  const selected = templates?.find((t) => t.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return;
    setDraft({
      name: selected.name,
      category: selected.category,
      subject: selected.subject,
      // A template written before the editor existed is plain text. It is
      // converted for editing but not saved back until the planner actually
      // changes something — opening a template must not rewrite it.
      body:
        selected.bodyFormat === 'html' ? selected.body : plainTextToHtml(selected.body),
      bodyFormat: 'html',
      openingVariants: selected.openingVariants ?? [],
      isDefault: selected.isDefault,
    });
    setDirty(false);
  }, [selected]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] });

  const create = useMutation({
    mutationFn: async () => {
      const res = await client.api.templates.$post({
        json: { ...BLANK, name: 'Untitled template', subject: 'Wedding Inquiry for {{couple_names}} — {{wedding_date}}' },
      });
      if (!res.ok) throw new Error('Failed to create template');
      return res.json();
    },
    // Awaited, so the new row is in the list before it is selected. Setting
    // the id first leaves `selected` null for a beat and the editor blinks
    // through "Pick a template" on the way to a template it already has.
    onSuccess: async (created) => {
      await invalidate();
      setDirty(false);
      selectTemplate((created as Template).id);
      focusName();
      toast.success('Template created');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const save = useMutation({
    mutationFn: async () => {
      const res = await client.api.templates[':id'].$put({
        param: { id: selectedId! },
        json: {
          name: draft.name.trim(),
          category: draft.category,
          subject: draft.subject,
          body: draft.body,
          bodyFormat: draft.bodyFormat,
          openingVariants: draft.openingVariants.filter((v) => v.trim().length > 0),
          isDefault: draft.isDefault,
        },
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? 'Failed to save template');
      }
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setDirty(false);
      toast.success('Template saved');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /**
   * "The same as my photographer one, but for florists" is how a second
   * template actually gets written, and the only tool for it was a near-blank
   * scaffold and retyping. This copies the *draft* rather than the stored row:
   * what is on screen is what a planner means by "this one", and a copy taken
   * mid-edit carries the edit instead of quietly reverting it.
   */
  const duplicate = useMutation({
    mutationFn: async () => {
      const res = await client.api.templates.$post({
        json: {
          name: `${draft.name.trim() || 'Untitled template'} (copy)`.slice(0, 100),
          category: draft.category,
          subject: draft.subject,
          body: draft.body,
          bodyFormat: draft.bodyFormat,
          openingVariants: draft.openingVariants.filter((v) => v.trim().length > 0),
          // One default per account is the point of the flag. A copy inherits
          // the words, not the standing.
          isDefault: false,
        },
      });
      if (!res.ok) throw new Error('Failed to duplicate template');
      return res.json();
    },
    onSuccess: async (created) => {
      await invalidate();
      setDirty(false);
      selectTemplate((created as Template).id);
      focusName();
      toast.success('Copied. Rename it below.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await client.api.templates[':id'].$delete({ param: { id: selectedId! } });
      if (!res.ok) throw new Error('Failed to delete template');
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      selectTemplate(null);
      toast.success('Template deleted');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  /**
   * The list as it is drawn. Templates are per-category, so this is two-level
   * data: sorted flat with the category repeated on every row, it asked the
   * planner to reconstruct the grouping by eye. The catch-alls lead — a
   * template that applies everywhere is not a peer of one category among
   * several — and each group sorts by name inside itself.
   */
  const groups = useMemo(() => {
    const byCategory = new Map<string | null, Template[]>();
    for (const t of templates ?? []) {
      const bucket = byCategory.get(t.category);
      if (bucket) bucket.push(t);
      else byCategory.set(t.category, [t]);
    }
    return [...byCategory.entries()]
      .sort(([a], [b]) => {
        if (a === null) return -1;
        if (b === null) return 1;
        return a.localeCompare(b);
      })
      .map(([category, rows]) => ({
        category,
        rows: [...rows].sort((x, y) => x.name.localeCompare(y.name)),
      }));
  }, [templates]);

  /** One category is not a grouping: a heading over the only group labels
   *  nothing and spends a line saying so. */
  const showHeadings = groups.length > 1;

  /** The same rows, flat, in the order they are drawn — what the arrows walk. */
  const rows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const canSave = draft.name.trim().length > 0 && draft.subject.trim().length > 0;

  /**
   * Opening a row is the one action on this page that can destroy work: the
   * draft is re-seeded from whatever is selected, so an unguarded click is a
   * silent discard of everything typed since the last save — no prompt, no
   * undo, and no sign in the list that there was anything to lose.
   */
  const openTemplate = (id: string) => {
    if (id === selectedId) return;
    if (dirty) {
      setPendingId(id);
      return;
    }
    selectTemplate(id);
  };

  /**
   * Arrow keys move focus, Enter opens. They cannot be one gesture: the
   * composer's picker is a radio group, where arrows change the selection, but
   * a selection change *here* loads a template and can raise the dialog above.
   * Passing over four rows to reach a fifth must not cost four loads and four
   * prompts. So: a listbox with one roving tab stop rather than a column of
   * buttons, each of which used to be a tab stop of its own.
   */
  const [focusId, setFocusId] = useState<string | null>(null);
  /** Null follows the selection, so the list is always entered at the template
   *  the editor is showing rather than at the top. */
  const focusedId = focusId ?? selectedId ?? rows[0]?.id ?? null;
  const optionRefs = useRef(new Map<string, HTMLAnchorElement>());

  const moveFocus = (to: number) => {
    // Clamped, not wrapped: a list that silently re-enters at the top reads as
    // a jump to somewhere else rather than as the end of the list.
    const next = rows[Math.max(0, Math.min(rows.length - 1, to))];
    if (!next) return;
    setFocusId(next.id);
    optionRefs.current.get(next.id)?.focus();
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const at = rows.findIndex((t) => t.id === focusedId);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveFocus(at + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocus(at - 1);
        break;
      case 'Home':
        e.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        e.preventDefault();
        moveFocus(rows.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (focusedId) openTemplate(focusedId);
        break;
    }
  };

  const saveAndSwitch = async () => {
    const next = pendingId;
    try {
      await save.mutateAsync();
    } catch {
      // `onError` has already named the problem. The dialog stays open over the
      // draft: a failed save is the worst possible moment to move on from it.
      return;
    }
    setPendingId(null);
    selectTemplate(next);
  };

  /**
   * Insert-at-cursor for the subject line.
   *
   * The body used to share this palette. It has its own now — inside the
   * editor, where a variable is a chip rather than a string splice — so this
   * one has narrowed to the single field that is still plain text. Two
   * palettes rather than one shared palette with a "last focused field" rule:
   * a planner clicking a chip should never have to remember where their cursor
   * was last.
   */
  const subjectRef = useRef<HTMLInputElement>(null);

  /** A template that has just been created or copied is named wrong by
   *  definition, so the caret starts in the name with the placeholder selected
   *  rather than in a field the planner has to go and find. */
  const nameRef = useRef<HTMLInputElement>(null);
  const focusName = () =>
    requestAnimationFrame(() => {
      nameRef.current?.focus();
      nameRef.current?.select();
    });

  const insertSubjectVariable = (name: string) => {
    const token = `{{${name}}}`;
    const el = subjectRef.current;
    const current = draft.subject;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);

    setDraft((d) => ({ ...d, subject: next }));
    setDirty(true);

    // Put the caret after what was just inserted, so a planner can keep typing.
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  /**
   * The sample wedding, with the planner's real name in it — because that is
   * what a real send merges. The sample's names are the fallback for a planner
   * who has not filled in settings: a preview reading "Hi Amy,
   * {{planner_name}}" reports a problem with the template that the template
   * does not have.
   *
   * Shared with the list, so a row's subject line and the preview's subject
   * line are the same sentence rendered once.
   */
  const previewContext: MergeContext = useMemo(
    () => ({
      ...SAMPLE,
      planner_name: settings?.name || SAMPLE.planner_name,
      planner_business: settings?.businessName || SAMPLE.planner_business,
    }),
    [settings?.name, settings?.businessName],
  );

  // ── Live preview: the finished email, not the template ───────────────────
  const preview = useMemo(() => {
    const context = previewContext;
    const bodyWithOpening = applyOpeningVariant(draft.body, draft.openingVariants, 0);
    const subject = renderTemplate(draft.subject, context);
    // Merged as html, so a sample value containing an ampersand is escaped
    // here exactly as it will be on the way to a vendor. Rendering the preview
    // through a different path from the send is how a preview starts lying.
    const body = renderTemplate(bodyWithOpening, context, draft.bodyFormat);

    // What `buildMessageParts` assembles on the way out: body, then signature,
    // in that order, in one document. The draft is always html by the time it
    // is on screen — a legacy plain-text template is converted when it opens —
    // so there is no second assembly order to get right here.
    const signature = settings?.emailSignatureHtml?.trim() ?? '';

    return { subject, body, html: body.text + signature };
  }, [
    draft.subject,
    draft.body,
    draft.bodyFormat,
    draft.openingVariants,
    previewContext,
    settings?.emailSignatureHtml,
  ]);

  /**
   * A row is its name. The merged subject sat under it for a while on the
   * theory that it was what tells two templates apart — but every subject this
   * product writes follows one shape, "<Category> Inquiry for <Couple> —
   * <Date>", so a column of them is one sentence repeated, truncating to the
   * same "…for Emma Smith & Li…". The only word that varies is the category,
   * and the group heading above the row already carries it. The whole email is
   * a click away in the preview.
   */
  const renderOption = (t: Template) => {
    const active = t.id === selectedId;
    return (
      <Link
        key={t.id}
        // A real href, so the row can be middle-clicked, cmd-clicked, or opened
        // from the context menu — writing a template means reading an older one,
        // and the two belong side by side. The link affordances come from this
        // being an anchor in the DOM; the role below does not affect them.
        to={templateHref(t.id)}
        // An option, not a button: the row is a choice within a set, and a
        // column of buttons was a column of tab stops. It costs the link role —
        // assistive tech announces "option" — which is the better trade than
        // handing a twenty-row list twenty tab stops back.
        role="option"
        aria-selected={active}
        tabIndex={t.id === focusedId ? 0 : -1}
        ref={(el) => {
          if (el) optionRefs.current.set(t.id, el);
          else optionRefs.current.delete(t.id);
        }}
        onClick={(e) => {
          // A modified click is a request for a second window, which is the
          // reason the row is a link at all. The browser serves it and this tab
          // is left exactly as it was, draft included. (Router `Link` bails on
          // modifiers too, so not calling `preventDefault` is enough.)
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          // Everything else goes through the guard rather than through the
          // href: navigating here re-seeds the draft, and an unguarded one
          // takes the unsaved work with it.
          e.preventDefault();
          setFocusId(t.id);
          openTemplate(t.id);
        }}
        // A long name truncates at this width; the full one has to be
        // reachable without opening the template.
        title={t.name}
        // `block`, because an anchor is inline and the row's padding is not.
        className={`block cursor-pointer rounded-2xl px-3 py-2.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40 ${
          // Selection and hover used to be the same fill, which left the list
          // with no way to say which template the editor was showing.
          active
            ? 'bg-[var(--color-primary-container)] font-medium'
            : 'hover:bg-[var(--color-surface-container-high)]'
        }`}
      >
        <span className="flex items-baseline gap-2">
          {active && dirty && (
            <>
              {/* Unsaved work belongs to the row, not to the editor: the list
                  is where it stands to be lost. */}
              <span
                aria-hidden
                className="size-1.5 shrink-0 -translate-y-px rounded-full bg-primary"
              />
              <span className="sr-only">Unsaved changes. </span>
            </>
          )}
          <span className="min-w-0 flex-1 truncate">{t.name}</span>
        </span>
      </Link>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <Link
            to="/settings"
            className="label-caps text-xs text-muted-foreground hover:text-foreground"
          >
            ← Settings
          </Link>
          <h1 className="display-tight text-3xl font-semibold">Templates</h1>
        </div>
        {/* Scoped to the tab it acts on. The ladder is one stored list with
            its own "Add an item" in the card header, so a second create button
            in the page header would be an offer to make a template that does
            not exist. */}
        {view === INQUIRIES_VIEW && (
          <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
            <Plus className="size-3.5" />
            New template
          </Button>
        )}
      </div>

      <Tabs value={view} onValueChange={openView} className="gap-6">
        {/* Underlined rather than a filled segmented control, matching the
            wedding hub: the page is already white cards on cream, and a
            champagne pill would outweigh the editor it sits above. */}
        <TabsList variant="line" className="w-full justify-start gap-6 p-0">
          <TabsTrigger value={INQUIRIES_VIEW} className={TAB_CLASS}>
            Inquiries
          </TabsTrigger>
          <TabsTrigger value={TIMELINE_VIEW} className={TAB_CLASS}>
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* Unmounted when it is not the open tab, not merely hidden: the
            editor holds a dirty draft and an unsaved-changes dialog, and
            neither should be live behind a tab the planner is not on. */}
        <TabsContent value={INQUIRIES_VIEW}>
          <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
            {/* ── The list ──────────────────────────────────────────────── */}
            {/* Pinned under the app header: the editor column runs to the message,
                the preview and the opening lines, and a list that scrolls away with
                it means scrolling back to the top to change templates. */}
            <Card className="scroll-slim h-fit rounded-3xl bg-card p-3 shadow-ambient lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
              {isLoading ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Loading…</p>
              ) : templates && templates.length > 0 ? (
                <div
                  role="listbox"
                  aria-label="Templates"
                  onKeyDown={onListKeyDown}
                  className="space-y-1"
                >
                  {groups.map((group) => {
                    const headingId = `t-group-${group.category ?? 'every'}`;
                    const options = group.rows.map((t) => renderOption(t));
                    return showHeadings ? (
                      <div
                        key={headingId}
                        role="group"
                        aria-labelledby={headingId}
                        className="space-y-1 pt-5 first:pt-0"
                      >
                        {/* Small, and outdented toward the card's edge while the
                            rows stay inset: a label and a row starting at the same
                            x, in the same rhythm, read as one alternating stripe of
                            text rather than as headings over things. The 20px above
                            against 4px below is the rest of it — a label belongs to
                            what follows it, not to what it follows. */}
                        <p
                          id={headingId}
                          className="label-caps px-1 text-[0.625rem] leading-none text-muted-foreground"
                        >
                          {group.category ?? 'Every category'}
                        </p>
                        {options}
                      </div>
                    ) : (
                      <Fragment key={headingId}>{options}</Fragment>
                    );
                  })}
                </div>
              ) : (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No templates yet.
                </p>
              )}
            </Card>

            {/* ── The editor ────────────────────────────────────────────── */}
            {selected ? (
              <div className="space-y-4">
                <Card className="gap-0 rounded-3xl bg-card p-5 shadow-ambient">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="t-name">Template name</Label>
                      <Input
                        id="t-name"
                        ref={nameRef}
                        value={draft.name}
                        onChange={(e) => {
                          setDraft({ ...draft, name: e.target.value });
                          setDirty(true);
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="t-category">Category</Label>
                      <Select
                        value={draft.category ?? ALL_CATEGORIES}
                        onValueChange={(v) => {
                          setDraft({ ...draft, category: v === ALL_CATEGORIES ? null : v });
                          setDirty(true);
                        }}
                      >
                        <SelectTrigger id="t-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL_CATEGORIES}>Every category</SelectItem>
                          {(categories ?? []).map((c) => (
                            <SelectItem key={c.id} value={c.name}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label htmlFor="t-subject">Subject</Label>
                    <VariableInput
                      id="t-subject"
                      ref={subjectRef}
                      value={draft.subject}
                      onChange={(e) => {
                        setDraft({ ...draft, subject: e.target.value });
                        setDirty(true);
                      }}
                    />
                  </div>

                  <div className="mt-4 space-y-2">
                    <Label htmlFor="t-body">Message</Label>
                    <RichBodyEditor
                      id="t-body"
                      ariaLabel="Message"
                      value={draft.body}
                      onChange={(html) => {
                        setDraft((d) => ({ ...d, body: html, bodyFormat: 'html' }));
                        setDirty(true);
                      }}
                    />
                  </div>

                  {/* ── Variable palette, for the subject ──────────────── */}
                  <div className="mt-4 space-y-2">
                    <p className="label-caps text-xs text-muted-foreground">
                      Subject variables — filled in at send
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {MERGE_VARIABLES.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => insertSubjectVariable(v)}
                          // Named for its destination: the message below has its
                          // own palette, and two buttons reading "Insert Vendor
                          // name" on one screen is ambiguous to a screen reader
                          // and to anyone driving this by keyboard.
                          aria-label={`Insert ${MERGE_VARIABLE_LABELS[v]} into subject`}
                          className="group inline-flex items-center gap-1 rounded-full bg-[var(--color-surface-container-high)] py-1 pr-3 pl-2 text-xs font-medium transition-colors hover:bg-[var(--color-primary-container)] hover:text-[var(--color-accent-foreground)]"
                        >
                          {/* The plus is the whole affordance: this is a thing you
                              place into the message, not a word you type. */}
                          <Plus className="size-3 text-muted-foreground transition-colors group-hover:text-[var(--color-accent-foreground)]" />
                          {MERGE_VARIABLE_LABELS[v]}
                        </button>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* ── Live preview ──────────────────────────────────────── */}
                <Card className="gap-0 rounded-3xl bg-card p-5 shadow-ambient">
                  <h2 className="display-tight text-lg font-semibold">Preview</h2>
                  <div className="mt-4 overflow-hidden rounded-2xl border border-border">
                    {/* The envelope, as a mail client shows it. Without a subject
                        line above the body, the field most likely to embarrass a
                        planner is the one field the preview never renders. */}
                    <div className="space-y-0.5 bg-[var(--color-surface-container-high)] px-4 py-3">
                      <p className="text-sm font-medium">
                        {preview.subject.text || 'No subject'}
                      </p>
                      <p className="text-xs text-muted-foreground">To: {SAMPLE_VENDOR}</p>
                    </div>
                    {/* The planner's own markup plus the signature the server
                        appends, rendered against mail-client defaults rather than
                        the app's — and re-sanitized server-side before it is ever
                        stored or sent. */}
                    <MailFrame
                      html={preview.html}
                      title="Email preview"
                      className="rounded-none border-0"
                    />
                  </div>
                </Card>

                {/* ── Opening variants ──────────────────────────────────── */}
                <Card className="gap-0 rounded-3xl bg-card p-5 shadow-ambient">
                  <h2 className="display-tight text-lg font-semibold">Opening lines</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Put <VariableChip name="opening" /> in the message and VendorOps rotates through
                    these, one per vendor, so no two emails open the same way.
                  </p>

                  <div className="mt-4 space-y-2">
                    {draft.openingVariants.map((variant, i) => (
                      <div key={i} className="flex gap-2">
                        <Input
                          aria-label={`Opening line ${i + 1}`}
                          value={variant}
                          onChange={(e) => {
                            const next = [...draft.openingVariants];
                            next[i] = e.target.value;
                            setDraft({ ...draft, openingVariants: next });
                            setDirty(true);
                          }}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`Remove opening line ${i + 1}`}
                          onClick={() => {
                            setDraft({
                              ...draft,
                              openingVariants: draft.openingVariants.filter((_, x) => x !== i),
                            });
                            setDirty(true);
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    ))}
                    {draft.openingVariants.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        None yet — <VariableChip name="opening" /> will simply be removed from the
                        message.
                      </p>
                    )}
                  </div>

                  <Button
                    className="mt-3 self-start"
                    variant="outline"
                    size="sm"
                    disabled={draft.openingVariants.length >= 10}
                    onClick={() => {
                      setDraft({ ...draft, openingVariants: [...draft.openingVariants, ''] });
                      setDirty(true);
                    }}
                  >
                    <Plus className="size-3.5" />
                    Add an opening line
                  </Button>
                </Card>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    Delete this template
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      disabled={duplicate.isPending}
                      onClick={() => duplicate.mutate()}
                    >
                      <Copy className="size-3.5" />
                      Duplicate
                    </Button>
                    <Button
                      disabled={!dirty || !canSave || save.isPending}
                      onClick={() => save.mutate()}
                    >
                      {save.isPending ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>

                {/* ── Leaving a template with edits still in it ──────────── */}
                <AlertDialog
                  open={pendingId !== null}
                  onOpenChange={(open) => {
                    if (!open) setPendingId(null);
                  }}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="display-tight text-2xl font-semibold">
                        Save “{draft.name.trim() || 'this template'}” first?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Opening another template loads it into this editor, and what you have
                        changed here has not been saved yet.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep editing</AlertDialogCancel>
                      <Button
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          const next = pendingId;
                          setPendingId(null);
                          selectTemplate(next);
                        }}
                      >
                        Discard changes
                      </Button>
                      <AlertDialogAction
                        disabled={!canSave || save.isPending}
                        onClick={(e) => {
                          // Radix closes the dialog on click. The switch has to
                          // wait for the PUT, and a save that fails has to leave
                          // the edits on screen rather than behind a shut dialog.
                          e.preventDefault();
                          void saveAndSwitch();
                        }}
                      >
                        {save.isPending ? 'Saving…' : 'Save and switch'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ) : (
              <Card className="rounded-3xl bg-card p-12 text-center shadow-ambient">
                <p className="display-tight text-lg font-semibold">Pick a template</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Or start a new one — the five you were given are a starting point, not a limit.
                </p>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value={TIMELINE_VIEW}>
          <TimelineTemplateCard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
